import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Newspaper, Loader2, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import { fetchDigestSettings, saveDigestSettings, sendDigestNow, fetchDigestPreview } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export const DigestSettingsRow = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["digest-settings"], queryFn: fetchDigestSettings });
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);

  const save = async (payload, ok) => {
    setBusy("save");
    try {
      qc.setQueryData(["digest-settings"], await saveDigestSettings(payload));
      toast.success(ok);
      setEditing(false);
      setEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      qc.invalidateQueries({ queryKey: ["digest-settings"] });
    } finally {
      setBusy("");
    }
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const r = await sendDigestNow();
      toast.success(`Digest for ${r.month} sent to ${r.sent_to_hint}`);
      qc.invalidateQueries({ queryKey: ["digest-settings"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setBusy("");
    }
  };

  const showPreview = async () => {
    setBusy("preview");
    try {
      setPreview(await fetchDigestPreview());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build preview");
    } finally {
      setBusy("");
    }
  };

  if (!data) return null;
  return (
    <div className="mt-6" data-testid="digest-section">
      <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5 mb-2">
        <Newspaper className="h-3.5 w-3.5" /> Monthly digest email
      </h4>
      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium">Spending, renewals & vault health on the 1st</div>
            <div className="text-xs text-muted-foreground" data-testid="digest-status">
              {data.enabled ? `On · next ${data.next_send}` : "Off"}{data.last_sent_at ? ` · last sent ${new Date(data.last_sent_at).toLocaleDateString()}` : ""}
            </div>
          </div>
          <Switch data-testid="digest-toggle" checked={data.enabled} disabled={busy === "save"} onCheckedChange={(v) => save({ enabled: v }, v ? "Monthly digest turned on" : "Monthly digest turned off")} />
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <Input data-testid="digest-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="bg-background h-9" autoFocus />
            <Button data-testid="digest-email-save-btn" size="sm" onClick={() => save({ email }, "Digest email saved")} disabled={busy === "save"}>{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex items-center justify-between text-xs">
            <span data-testid="digest-email-hint" className={data.email_hint ? "" : "text-muted-foreground"}>
              {data.email_hint ? `Sends to ${data.email_hint}${data.uses_recovery_email ? " (recovery email)" : ""}` : "No email yet — add one to receive the digest"}
            </span>
            <button data-testid="digest-email-edit-btn" onClick={() => setEditing(true)} className="text-primary hover:underline">{data.email_hint ? "Change" : "Add"}</button>
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <Button data-testid="digest-preview-btn" size="sm" variant="outline" className="h-8 gap-1.5" onClick={showPreview} disabled={!!busy}>{busy === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Preview</Button>
          <Button data-testid="digest-send-btn" size="sm" variant="outline" className="h-8 gap-1.5" onClick={sendNow} disabled={!!busy || !data.email_hint}>{busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send me one now</Button>
        </div>
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="digest-preview-dialog">
          <DialogHeader><DialogTitle className="font-serif">Digest preview · {preview?.data.month_label}</DialogTitle></DialogHeader>
          {preview && <div className="rounded-xl overflow-hidden border border-border" dangerouslySetInnerHTML={{ __html: preview.html }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
